import type { CategoriaPendencia, ResumoDivergencia, StatusItemConferencia } from '../types/conferencia';

/**
 * Espelha StatusItemConferencia.Calcular (backend/Services/Conferencia/StatusItemConferencia.cs)
 * para montar patch otimista em useAtualizarItem — o servidor recalcula com autoridade e o
 * onSettled sempre revalida, então uma divergência aqui nunca "gruda" no cache.
 */
export function statusItemOtimista(
  qtdNota: number,
  qtdConferida: number,
  qtdAvariada: number
): StatusItemConferencia {
  if (qtdAvariada > 0) return 'avaria';
  const totalRecebido = qtdConferida + qtdAvariada;
  if (totalRecebido === 0) return 'pendente';
  if (totalRecebido < qtdNota) return 'falta';
  if (totalRecebido > qtdNota) return 'excedente';
  return 'ok';
}

export type ToneConferencia = 'ok' | 'warning' | 'danger' | 'neutral' | 'accent';

export function statusItemConferencia(status: string): { label: string; tone: ToneConferencia } {
  switch (status) {
    case 'ok': return { label: 'OK', tone: 'ok' };
    case 'falta': return { label: 'Falta', tone: 'warning' };
    case 'excedente': return { label: 'Excedente', tone: 'accent' };
    case 'avaria': return { label: 'Avaria', tone: 'danger' };
    default: return { label: '—', tone: 'neutral' };
  }
}

/**
 * Espelha LimiteConferencia.Aplicar (backend/Services/Conferencia/LimiteConferencia.cs).
 * Roda ANTES do PUT pra o feedback ser instantâneo na doca; o servidor reaplica a regra com
 * autoridade — isto aqui é UX, não segurança.
 *
 * Teto conforme `permitirExcedente`:
 * - `true` (o operador confirmou o excedente): número acima da nota é declaração deliberada,
 *   grava como está e o item vira `excedente`. A UI SEMPRE confirma antes de passar `true` —
 *   tanto na digitação quanto na BIPAGEM (bipar acima da nota abre o mesmo diálogo).
 * - `false` (não confirmado): estourar ZERA a contagem (`zerado`). Na prática a UI nunca manda
 *   `false` com estouro — quando o operador cancela a confirmação, ela simplesmente não
 *   persiste. É uma guarda de servidor contra request malformado. A avaria sobrevive à zeragem.
 * `zerado` e `excedente` nunca são true juntos.
 */
export function aplicarLimiteConferencia(
  qtdNota: number,
  qtdConferida: number,
  qtdAvariada: number,
  permitirExcedente: boolean
): { qtdConferida: number; qtdAvariada: number; zerado: boolean; excedente: boolean } {
  // Clamp antes de somar: avaria negativa derrubaria o total abaixo do teto e deixaria
  // passar uma contagem absurda (mesmo motivo do clamp no backend).
  const conferida = qtdConferida > 0 ? qtdConferida : 0;
  const avariada = qtdAvariada > 0 ? qtdAvariada : 0;
  const estoura = conferida + avariada > qtdNota;

  if (estoura && !permitirExcedente) {
    return { qtdConferida: 0, qtdAvariada: avariada, zerado: true, excedente: false };
  }
  return { qtdConferida: conferida, qtdAvariada: avariada, zerado: false, excedente: estoura };
}

/** Onde um código de barras cai: qual item da nota e quantas unidades base ele soma. */
export interface DestinoLeitura {
  itemId: number;
  /** Unidades base que esta leitura soma (QTDEEMBALAGEM da variante lida). */
  unidades: number;
  /** Rótulo da embalagem lida ("UN", "CX") — vai no aviso de confirmação do bipe. */
  unidade: string | null;
}

/**
 * Índice EAN → destino da leitura, montado uma vez a partir dos itens já carregados. É o que
 * deixa a bipagem ser instantânea e offline-tolerante (nenhum round-trip por scan) e o que
 * permite bipar caixa OU unidade indiferentemente: cada item traz TODAS as embalagens da
 * família (mesmo PRODUTOPRINCIPAL), e o código lido escolhe o multiplicador.
 *
 * EAN repetido fica com o primeiro item — um mesmo código não pode somar em dois lugares.
 */
export function indexarEmbalagensPorEan(
  itens: {
    id: number;
    ean: string | null;
    embalagens?: { ean: string | null; qtdEmbalagem: number; unidade: string | null }[];
  }[]
): Map<string, DestinoLeitura> {
  const indice = new Map<string, DestinoLeitura>();

  const registrar = (ean: string | null | undefined, destino: DestinoLeitura) => {
    const chave = ean?.trim();
    if (!chave || indice.has(chave)) return;
    indice.set(chave, destino);
  };

  for (const item of itens) {
    const embalagens = item.embalagens ?? [];

    for (const e of embalagens) {
      registrar(e.ean, {
        itemId: item.id,
        // Embalagem zerada/negativa vira 1: somar 0 por leitura deixaria o operador
        // bipando pra sempre sem a contagem sair do lugar.
        unidades: e.qtdEmbalagem > 0 ? e.qtdEmbalagem : 1,
        unidade: e.unidade,
      });
    }

    // Item sem família cadastrada (manifesto sem produto interno casado) ainda precisa ser
    // bipável pelo EAN da própria nota — senão não há como conferir a linha.
    registrar(item.ean, { itemId: item.id, unidades: 1, unidade: null });
  }

  return indice;
}

/**
 * Recomputa o ResumoDivergencia no CLIENTE a partir dos itens já carregados — espelha
 * ResumoDivergenciaConferencia.Calcular (backend/Services/Conferencia/ResumoDivergenciaConferencia.cs).
 * `ConferenciaNfe` (detalhe, GET /conferencia-nfe/{id}) não traz um `resumo` pronto como
 * `ConferenciaListItem` traz — o workspace de bipagem (Task 15) recalcula aqui a cada render
 * pra o painel de divergência acompanhar o patch otimista de useAtualizarItem instantaneamente,
 * sem esperar round-trip nem duplicar a regra de negócio (só conta os mesmos status strings).
 */
export function resumoDivergenciaLocal(itens: { status: string }[]): ResumoDivergencia {
  const total = itens.length;
  const pendentes = itens.filter((i) => i.status === 'pendente').length;
  return {
    total,
    conferidos: total - pendentes,
    pendentes,
    falta: itens.filter((i) => i.status === 'falta').length,
    excedente: itens.filter((i) => i.status === 'excedente').length,
    avaria: itens.filter((i) => i.status === 'avaria').length,
  };
}

/**
 * Pré-marcação das categorias de pendência a partir do resumo — espelha
 * CategoriasPendencia.Sugerir no backend. `divergencia_qtd` e `outro` são sempre escolha
 * manual do conferente (nunca pré-marcados).
 */
export function sugerirCategoriasPendencia(resumo: ResumoDivergencia): CategoriaPendencia[] {
  const sugeridas: CategoriaPendencia[] = [];
  if (resumo.avaria > 0) sugeridas.push('avaria');
  if (resumo.excedente > 0) sugeridas.push('excesso');
  if (resumo.falta > 0) sugeridas.push('falta_volumes');
  return sugeridas;
}

/**
 * Coluna do Kanban de Pendências para uma nota, a partir das categorias declaradas
 * (`pendenciaCategorias`, CSV). Categoria ÚNICA → a coluna dela; 2+ categorias distintas →
 * `'multiplas'` (o card fica numa coluna só, sem duplicar — decisão de design). Vazio/null →
 * `null` (não entra no board; na prática toda pendência tem ≥1 categoria).
 */
export function colunaDaPendencia(pendenciaCategorias: string | null | undefined): string | null {
  const cats = [...new Set((pendenciaCategorias ?? '').split(',').map((c) => c.trim()).filter(Boolean))];
  if (cats.length === 0) return null;
  return cats.length === 1 ? cats[0] : 'multiplas';
}
